import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import './index.css';
import {
  GET_CAPITAL_OFFICE_DATA,
  UPDATE_CAPITAL_OFFICE_DATA,
  DELETE_CAPITAL_OFFICE_DATA,
} from '../../api/capitalOffice.js';

const STATUS_OPTIONS = [
  'Pending',
  'Under Review',
  'Contacted',
  'Accepted',
  'Rejected',
];

const STAGE_OPTIONS = [
  'Pre-seed',
  'Seed',
  'Series A',
  'Series B+',
];

const SECTOR_OPTIONS = [
  'B2B SaaS',
  'Fintech',
  'Healthtech',
  'Consumer Tech',
  'Deep Tech',
  'Other',
];

const CapitalOfficeAdminView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // View modal state
  const [viewItem, setViewItem] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Edit modal state
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(GET_CAPITAL_OFFICE_DATA);
      if (res.data && Array.isArray(res.data.data)) {
        setData(res.data.data);
      } else if (Array.isArray(res.data)) {
        setData(res.data);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error fetching Capital Office submissions:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered list
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        searchTerm === '' ||
        (item.founderName &&
          item.founderName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.companyName &&
          item.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.workEmail &&
          item.workEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.sector &&
          item.sector.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.headquarters &&
          item.headquarters.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.roundStage &&
          item.roundStage.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === 'All' || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = data.length;
    const pending = data.filter((d) => d.status === 'Pending' || !d.status).length;
    const underReview = data.filter((d) => d.status === 'Under Review').length;
    const accepted = data.filter((d) => d.status === 'Accepted').length;
    return { total, pending, underReview, accepted };
  }, [data]);

  // Handlers for View
  const handleOpenView = (item) => {
    setViewItem(item);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewItem(null);
  };

  // Handlers for Edit
  const handleOpenEdit = (item) => {
    setEditItem({
      _id: item._id,
      founderName: item.founderName || '',
      workEmail: item.workEmail || '',
      companyName: item.companyName || '',
      website: item.website || '',
      headquarters: item.headquarters || '',
      sector: item.sector || '',
      oneLineDesc: item.oneLineDesc || '',
      roundStage: item.roundStage || '',
      targetRaise: item.targetRaise || '',
      capitalRaised: item.capitalRaised || '',
      targetCloseDate: item.targetCloseDate || '',
      valuationApproach: item.valuationApproach || '',
      currentCommitments: item.currentCommitments || '',
      leadStatus: item.leadStatus || '',
      roundContext: item.roundContext || '',
      businessModel: item.businessModel || '',
      monthlyRevenue: item.monthlyRevenue || '',
      revenuePeriod: item.revenuePeriod || '',
      runwayRemaining: item.runwayRemaining || '',
      teamSize: item.teamSize || '',
      proofPoint: item.proofPoint || '',
      targetGeography: item.targetGeography || '',
      idealInvestor: item.idealInvestor || '',
      status: item.status || 'Pending',
      adminNotes: item.adminNotes || '',
    });
    setEditError('');
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditItem(null);
    setEditError('');
  };

  const handleEditChange = (field, value) => {
    setEditItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editItem || !editItem._id) return;
    setEditLoading(true);
    setEditError('');

    try {
      const updateUrl = `${UPDATE_CAPITAL_OFFICE_DATA}/${editItem._id}`;
      const payload = {
        founderName: editItem.founderName,
        workEmail: editItem.workEmail,
        companyName: editItem.companyName,
        website: editItem.website,
        headquarters: editItem.headquarters,
        sector: editItem.sector,
        oneLineDesc: editItem.oneLineDesc,
        roundStage: editItem.roundStage,
        targetRaise: editItem.targetRaise,
        capitalRaised: editItem.capitalRaised,
        targetCloseDate: editItem.targetCloseDate,
        valuationApproach: editItem.valuationApproach,
        currentCommitments: editItem.currentCommitments,
        leadStatus: editItem.leadStatus,
        roundContext: editItem.roundContext,
        businessModel: editItem.businessModel,
        monthlyRevenue: editItem.monthlyRevenue,
        revenuePeriod: editItem.revenuePeriod,
        runwayRemaining: editItem.runwayRemaining,
        teamSize: editItem.teamSize,
        proofPoint: editItem.proofPoint,
        targetGeography: editItem.targetGeography,
        idealInvestor: editItem.idealInvestor,
        status: editItem.status,
        adminNotes: editItem.adminNotes,
      };

      const res = await axios.put(updateUrl, payload);
      const updated = res.data.data;

      setData((prev) =>
        prev.map((item) => (item._id === updated._id ? updated : item))
      );

      if (viewItem && viewItem._id === updated._id) {
        setViewItem(updated);
      }

      handleCloseEdit();
    } catch (err) {
      console.error('Failed to update Capital Office entry:', err);
      setEditError(
        err.response?.data?.details ||
        err.response?.data?.error ||
        'Failed to save updates'
      );
    } finally {
      setEditLoading(false);
    }
  };

  // Handlers for Delete
  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteId(null);
    setDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${DELETE_CAPITAL_OFFICE_DATA}/${deleteId}`);
      setData((prev) => prev.filter((item) => item._id !== deleteId));
      if (viewItem && viewItem._id === deleteId) {
        handleCloseView();
      }
      handleCloseDelete();
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert('Failed to delete application submission');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!data.length) {
      alert('No data available to export');
      return;
    }

    const headers = [
      'Submission ID',
      'Founder Name',
      'Work Email',
      'Company Name',
      'Website',
      'Headquarters',
      'Sector',
      'Round Stage',
      'Target Raise (USD)',
      'Capital Raised (USD)',
      'Target Close Date',
      'Valuation Approach',
      'Lead Investor Status',
      'Business Model',
      'Monthly Revenue (USD)',
      'Runway Remaining',
      'Target Geography',
      'Attached Documents Count',
      'Data Room Link',
      'Status',
      'Created At',
    ];

    const rows = filteredData.map((item) => [
      `"${item._id || ''}"`,
      `"${(item.founderName || '').replace(/"/g, '""')}"`,
      `"${(item.workEmail || '').replace(/"/g, '""')}"`,
      `"${(item.companyName || '').replace(/"/g, '""')}"`,
      `"${(item.website || '').replace(/"/g, '""')}"`,
      `"${(item.headquarters || '').replace(/"/g, '""')}"`,
      `"${(item.sector || '').replace(/"/g, '""')}"`,
      `"${(item.roundStage || '').replace(/"/g, '""')}"`,
      `"${(item.targetRaise || '').replace(/"/g, '""')}"`,
      `"${(item.capitalRaised || '').replace(/"/g, '""')}"`,
      `"${(item.targetCloseDate || '').replace(/"/g, '""')}"`,
      `"${(item.valuationApproach || '').replace(/"/g, '""')}"`,
      `"${(item.leadStatus || '').replace(/"/g, '""')}"`,
      `"${(item.businessModel || '').replace(/"/g, '""')}"`,
      `"${(item.monthlyRevenue || '').replace(/"/g, '""')}"`,
      `"${(item.runwayRemaining || '').replace(/"/g, '""')}"`,
      `"${(item.targetGeography || '').replace(/"/g, '""')}"`,
      `"${item.documents ? item.documents.length : 0}"`,
      `"${(item.dataRoomLink || '').replace(/"/g, '""')}"`,
      `"${(item.status || 'Pending').replace(/"/g, '""')}"`,
      `"${item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `Capital_Office_Applications_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusChip = (status) => {
    const val = status || 'Pending';
    let className = 'chip-status-pending';
    if (val === 'Under Review') className = 'chip-status-under-review';
    if (val === 'Contacted') className = 'chip-status-contacted';
    if (val === 'Accepted') className = 'chip-status-accepted';
    if (val === 'Rejected') className = 'chip-status-rejected';

    return <Chip label={val} className={`chip-status ${className}`} size="small" />;
  };

  return (
    <Box className="capital-admin-wrapper">
      {/* Top Header */}
      <Box className="capital-admin-header">
        <Box>
          <Typography variant="h5" className="capital-page-title">
            Capital Office Applications
          </Typography>
          <Typography variant="body2" className="capital-page-subtitle">
            Manage, evaluate, and track founder round applications for curated investor matchmaking.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            className="capital-btn-refresh"
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            className="capital-btn-export"
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* KPI Cards Grid */}
      <Grid container spacing={2.5} className="capital-stats-grid">
        <Grid item xs={12} sm={6} md={3}>
          <Box className="capital-stat-card">
            <Box className="capital-stat-icon stat-icon-total">
              <AccountBalanceIcon />
            </Box>
            <Box>
              <Box className="capital-stat-value">{stats.total}</Box>
              <Box className="capital-stat-label">Total Applications</Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box className="capital-stat-card">
            <Box className="capital-stat-icon stat-icon-pending">
              <HourglassEmptyIcon />
            </Box>
            <Box>
              <Box className="capital-stat-value">{stats.pending}</Box>
              <Box className="capital-stat-label">Pending Review</Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box className="capital-stat-card">
            <Box className="capital-stat-icon stat-icon-review">
              <BusinessIcon />
            </Box>
            <Box>
              <Box className="capital-stat-value">{stats.underReview}</Box>
              <Box className="capital-stat-label">Under Active Review</Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box className="capital-stat-card">
            <Box className="capital-stat-icon stat-icon-accepted">
              <CheckCircleOutlineIcon />
            </Box>
            <Box>
              <Box className="capital-stat-value">{stats.accepted}</Box>
              <Box className="capital-stat-label">Accepted Mandates</Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Filters Bar */}
      <Box className="capital-filter-bar">
        <TextField
          size="small"
          placeholder="Search by company, founder, email, sector..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="capital-search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status Filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 170, backgroundColor: '#ffffff', borderRadius: '6px' }}
        >
          <MenuItem value="All">All Statuses ({data.length})</MenuItem>
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt} ({data.filter((d) => (d.status || 'Pending') === opt).length})
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper} className="capital-table-paper">
        <Table sx={{ minWidth: 850 }}>
          <TableHead className="capital-table-head">
            <TableRow>
              <TableCell className="capital-head-cell">Company & Founder</TableCell>
              <TableCell className="capital-head-cell">Round & Target</TableCell>
              <TableCell className="capital-head-cell">Sector & Model</TableCell>
              <TableCell className="capital-head-cell">Traction Signals</TableCell>
              <TableCell className="capital-head-cell">Investor Fit</TableCell>
              <TableCell className="capital-head-cell">Status</TableCell>
              <TableCell className="capital-head-cell">Submitted</TableCell>
              <TableCell className="capital-head-cell" align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} sx={{ color: '#5c4eb4' }} />
                  <Typography variant="body2" sx={{ mt: 1.5, color: '#64748b' }}>
                    Loading Capital Office applications...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                    No applications found matching your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow key={row._id} className="capital-table-row">
                  <TableCell>
                    <div className="company-col-title">{row.companyName}</div>
                    <div className="company-col-founder">
                      {row.founderName} • <span className="text-email">{row.workEmail}</span>
                    </div>
                    <div className="company-col-location">
                      📍 {row.headquarters || 'Not specified'}
                      {Array.isArray(row.documents) && row.documents.length > 0 && (
                        <span style={{ marginLeft: '8px', color: '#5c4eb4', fontWeight: 600 }}>
                          📎 {row.documents.length} doc{row.documents.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {row.dataRoomLink && (
                        <span style={{ marginLeft: '6px', color: '#0284c7', fontWeight: 600 }}>
                          🔒 Room
                        </span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="target-val">{row.roundStage} · USD {row.targetRaise}</div>
                    <div className="text-stage">Close: {row.targetCloseDate}</div>
                  </TableCell>

                  <TableCell>
                    <span className="badge-sector">{row.sector}</span>
                    <div className="text-stage">{row.businessModel}</div>
                  </TableCell>

                  <TableCell>
                    <div className="mrr-val">MRR: USD {row.monthlyRevenue}</div>
                    <div className="text-stage">Runway: {row.runwayRemaining}</div>
                  </TableCell>

                  <TableCell>
                    <div className="company-col-title" style={{ fontSize: '13px' }}>
                      {row.targetGeography}
                    </div>
                    <div className="text-stage">
                      {Array.isArray(row.investorTypes) ? row.investorTypes.slice(0, 2).join(', ') : ''}
                    </div>
                  </TableCell>

                  <TableCell>{getStatusChip(row.status)}</TableCell>

                  <TableCell sx={{ fontSize: '12.5px', color: '#64748b' }}>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'}
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title="View Full Application">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenView(row)}
                        sx={{ color: '#0284c7' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Application / Status">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                        sx={{ color: '#5c4eb4' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Application">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDelete(row._id)}
                        sx={{ color: '#ef4444' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ======================= VIEW DIALOG ======================= */}
      <Dialog
        open={viewOpen}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        {viewItem && (
          <>
            <DialogTitle className="detail-modal-header">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  {viewItem.companyName}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Applied by {viewItem.founderName} ({viewItem.workEmail})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {getStatusChip(viewItem.status)}
              </Box>
            </DialogTitle>

            <DialogContent className="detail-modal-content" dividers>
              {/* STEP 1: COMPANY PROFILE */}
              <Typography className="detail-section-title">
                1. Company Profile
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Founder Name</div>
                    <div className="detail-field-value">{viewItem.founderName}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Work Email</div>
                    <div className="detail-field-value text-email">{viewItem.workEmail}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Website</div>
                    <div className="detail-field-value">
                      {viewItem.website ? (
                        <a href={viewItem.website.startsWith('http') ? viewItem.website : `https://${viewItem.website}`} target="_blank" rel="noreferrer" style={{ color: '#5c4eb4' }}>
                          {viewItem.website}
                        </a>
                      ) : 'N/A'}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Headquarters</div>
                    <div className="detail-field-value">{viewItem.headquarters}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Sector</div>
                    <div className="detail-field-value">{viewItem.sector}</div>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">One-Line Description</div>
                    <div className="detail-field-value long-text">{viewItem.oneLineDesc}</div>
                  </Box>
                </Grid>
              </Grid>

              {/* STEP 2: ROUND DETAILS */}
              <Typography className="detail-section-title">
                2. Round Parameters
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Round Stage</div>
                    <div className="detail-field-value">{viewItem.roundStage}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Target Raise</div>
                    <div className="detail-field-value">USD {viewItem.targetRaise}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Capital Raised to Date</div>
                    <div className="detail-field-value">{viewItem.capitalRaised ? `USD ${viewItem.capitalRaised}` : 'None'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Target Close Date</div>
                    <div className="detail-field-value">{viewItem.targetCloseDate}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Valuation Approach</div>
                    <div className="detail-field-value">{viewItem.valuationApproach || 'Not specified'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Lead Investor Status</div>
                    <div className="detail-field-value">{viewItem.leadStatus || 'Not started'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Current Commitments</div>
                    <div className="detail-field-value">{viewItem.currentCommitments || 'None'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Use of Funds</div>
                    <div className="tags-container">
                      {Array.isArray(viewItem.useOfFunds) && viewItem.useOfFunds.length > 0 ? (
                        viewItem.useOfFunds.map((u) => <span key={u} className="support-tag">{u}</span>)
                      ) : (
                        <span style={{ fontSize: '13px', color: '#64748b' }}>None specified</span>
                      )}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Round Context & Milestones</div>
                    <div className="detail-field-value long-text">{viewItem.roundContext}</div>
                  </Box>
                </Grid>
              </Grid>

              {/* STEP 3: TRACTION SIGNALS */}
              <Typography className="detail-section-title">
                3. Traction & Metrics
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Business Model</div>
                    <div className="detail-field-value">{viewItem.businessModel}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Monthly Revenue</div>
                    <div className="detail-field-value" style={{ color: '#10b981' }}>USD {viewItem.monthlyRevenue}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Revenue Period</div>
                    <div className="detail-field-value">{viewItem.revenuePeriod}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">12M Growth</div>
                    <div className="detail-field-value">{viewItem.revenueGrowth || 'N/A'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Gross Margin</div>
                    <div className="detail-field-value">{viewItem.grossMargin || 'N/A'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Active Customers</div>
                    <div className="detail-field-value">{viewItem.activeCustomers || 'N/A'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Monthly Burn</div>
                    <div className="detail-field-value">{viewItem.monthlyBurn ? `USD ${viewItem.monthlyBurn}` : 'N/A'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Runway Remaining</div>
                    <div className="detail-field-value">{viewItem.runwayRemaining}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Team Size</div>
                    <div className="detail-field-value">{viewItem.teamSize || 'N/A'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Key Traction Signals</div>
                    <div className="tags-container">
                      {Array.isArray(viewItem.tractionSignals) && viewItem.tractionSignals.length > 0 ? (
                        viewItem.tractionSignals.map((t) => <span key={t} className="support-tag">{t}</span>)
                      ) : (
                        <span style={{ fontSize: '13px', color: '#64748b' }}>None</span>
                      )}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Strongest Proof Point</div>
                    <div className="detail-field-value long-text">{viewItem.proofPoint}</div>
                  </Box>
                </Grid>
              </Grid>

              {/* STEP 4: INVESTOR FIT */}
              <Typography className="detail-section-title">
                4. Investor Fit & Preferences
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Target Geography</div>
                    <div className="detail-field-value">{viewItem.targetGeography}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Cheque Range (Min – Max)</div>
                    <div className="detail-field-value">
                      {viewItem.minCheque || '0'} – {viewItem.maxCheque || 'Open'} USD
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Preferred Investor Types</div>
                    <div className="tags-container">
                      {Array.isArray(viewItem.investorTypes) && viewItem.investorTypes.length > 0 ? (
                        viewItem.investorTypes.map((i) => <span key={i} className="support-tag">{i}</span>)
                      ) : (
                        <span>Open</span>
                      )}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Strategic Value Sought</div>
                    <div className="tags-container">
                      {Array.isArray(viewItem.strategicValue) && viewItem.strategicValue.length > 0 ? (
                        viewItem.strategicValue.map((s) => <span key={s} className="support-tag">{s}</span>)
                      ) : (
                        <span>Open</span>
                      )}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Existing Conversations</div>
                    <div className="detail-field-value">{viewItem.existingConversations || 'None yet'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Investor Exclusions / Conflicts</div>
                    <div className="detail-field-value">{viewItem.investorExclusions || 'None'}</div>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="detail-field-box">
                    <div className="detail-field-label">Ideal Investor Profile</div>
                    <div className="detail-field-value long-text">{viewItem.idealInvestor}</div>
                  </Box>
                </Grid>
              </Grid>

              {/* STEP 5: ATTACHED DOCUMENTS & DILIGENCE MATERIALS */}
              <Typography className="detail-section-title">
                5. Attached Documents & Diligence Materials
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {Array.isArray(viewItem.documents) && viewItem.documents.length > 0 ? (
                  viewItem.documents.map((doc, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box className="detail-field-box" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ overflow: 'hidden' }}>
                          <div className="detail-field-label">
                            {doc.docType || 'Document'} • {doc.size || 'S3 File'}
                          </div>
                          <div
                            className="detail-field-value"
                            style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '240px' }}
                            title={doc.name}
                          >
                            {doc.name.toLowerCase().endsWith('.pdf') ? '📄 ' : doc.name.toLowerCase().endsWith('.xlsx') || doc.name.toLowerCase().endsWith('.xls') || doc.name.toLowerCase().endsWith('.csv') ? '📊 ' : '📁 '}
                            {doc.name}
                          </div>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FileDownloadIcon />}
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ textTransform: 'none', minWidth: '130px', fontSize: '11.5px', borderColor: '#5c4eb4', color: '#5c4eb4', '&:hover': { borderColor: '#4a3d99', backgroundColor: '#f5f3ff' } }}
                        >
                          View Document
                        </Button>
                      </Box>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12} sm={viewItem.dataRoomLink ? 6 : 12}>
                    <Box className="detail-field-box">
                      <div className="detail-field-label">Uploaded Files</div>
                      <div className="detail-field-value" style={{ color: '#94a3b8', fontWeight: 400 }}>No documents uploaded</div>
                    </Box>
                  </Grid>
                )}

                {viewItem.dataRoomLink && (
                  <Grid item xs={12} sm={6}>
                    <Box className="detail-field-box" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ overflow: 'hidden' }}>
                        <div className="detail-field-label">🔒 Data Room</div>
                        <div
                          className="detail-field-value"
                          style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '240px' }}
                          title={viewItem.dataRoomLink}
                        >
                          {viewItem.dataRoomLink}
                        </div>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        href={viewItem.dataRoomLink.startsWith('http') ? viewItem.dataRoomLink : `https://${viewItem.dataRoomLink}`}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ textTransform: 'none', minWidth: '110px', fontSize: '11.5px', borderColor: '#5c4eb4', color: '#5c4eb4', '&:hover': { borderColor: '#4a3d99', backgroundColor: '#f5f3ff' } }}
                      >
                        Open Room ↗
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* ADMIN NOTES */}
              {viewItem.adminNotes && (
                <>
                  <Typography className="detail-section-title">Admin Notes</Typography>
                  <Box className="detail-field-box" sx={{ mb: 2 }}>
                    <div className="detail-field-value long-text">{viewItem.adminNotes}</div>
                  </Box>
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={handleCloseView} sx={{ textTransform: 'none', color: '#64748b' }}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleCloseView();
                  handleOpenEdit(viewItem);
                }}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#5c4eb4',
                  '&:hover': { backgroundColor: '#4a3d99' },
                }}
              >
                Edit Details / Status
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ======================= EDIT DIALOG ======================= */}
      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        {editItem && (
          <>
            <DialogTitle className="detail-modal-header">
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Edit Application: {editItem.companyName}
              </Typography>
            </DialogTitle>

            <DialogContent className="detail-modal-content" dividers>
              {editError && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '13px' }}>
                  {editError}
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Application Status"
                    size="small"
                    value={editItem.status}
                    onChange={(e) => handleEditChange('status', e.target.value)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    size="small"
                    value={editItem.companyName}
                    onChange={(e) => handleEditChange('companyName', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Founder Name"
                    size="small"
                    value={editItem.founderName}
                    onChange={(e) => handleEditChange('founderName', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Work Email"
                    size="small"
                    value={editItem.workEmail}
                    onChange={(e) => handleEditChange('workEmail', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Headquarters"
                    size="small"
                    value={editItem.headquarters}
                    onChange={(e) => handleEditChange('headquarters', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Sector"
                    size="small"
                    value={editItem.sector}
                    onChange={(e) => handleEditChange('sector', e.target.value)}
                  >
                    {SECTOR_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Round Stage"
                    size="small"
                    value={editItem.roundStage}
                    onChange={(e) => handleEditChange('roundStage', e.target.value)}
                  >
                    {STAGE_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Target Raise"
                    size="small"
                    value={editItem.targetRaise}
                    onChange={(e) => handleEditChange('targetRaise', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Target Close Date"
                    size="small"
                    value={editItem.targetCloseDate}
                    onChange={(e) => handleEditChange('targetCloseDate', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monthly Revenue (MRR)"
                    size="small"
                    value={editItem.monthlyRevenue}
                    onChange={(e) => handleEditChange('monthlyRevenue', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Runway Remaining"
                    size="small"
                    value={editItem.runwayRemaining}
                    onChange={(e) => handleEditChange('runwayRemaining', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Target Geography"
                    size="small"
                    value={editItem.targetGeography}
                    onChange={(e) => handleEditChange('targetGeography', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Admin Notes / Review Feedback"
                    placeholder="Add confidential admin notes, mandate matches, review next steps..."
                    value={editItem.adminNotes}
                    onChange={(e) => handleEditChange('adminNotes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={handleCloseEdit} sx={{ textTransform: 'none', color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={editLoading}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#5c4eb4',
                  '&:hover': { backgroundColor: '#4a3d99' },
                }}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ======================= DELETE CONFIRMATION DIALOG ======================= */}
      <Dialog
        open={deleteOpen}
        onClose={handleCloseDelete}
        PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Delete Application?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            Are you sure you want to permanently delete this Capital Office application? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDelete} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ textTransform: 'none' }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CapitalOfficeAdminView;
